import React, { useMemo } from "react";
import { colors2, SkeletonLoader } from "oolib";
import { UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { SidePanel } from "@/components/UI/SidePanel";
import { useGetTopicClusteringTreeCounts } from "@/utils/react-query-hooks/aiAnnotation";
import { CardGridLayout } from "@/components/analysisModules/TopicClusteringTreeView/CardGridLayout";
import { toCardTree } from "./toCardTree";
import { activeFiltersFor, previousWindowFiltersFor } from "./scopeFilters";
import { periodKey } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/periodKeys";

/**
 * A topic's landing page — the Topics page's drilldown body, exactly — in a
 * side panel, as one level of a SidePanelStack (the Overview's Topics
 * widget opens it first; a category card's topic list opens it second).
 * A sub-topic card opens the row panel beside it (onOpenRow).
 *
 * Owns the one request the body needs and the page would otherwise make:
 * the tree counts over the scope, which give the topic with its sub-topics
 * and the signal meta. The drilldown's lines and the "vs last period" chip
 * are requested by the components that draw them, as on the page.
 *
 *   isOpen, width
 *   topicNodeId          the topic to show
 *   scope                { period, comparePeriod, extraFilters, topicClusteringLens, dateFieldMap }
 *   titleByContentType
 *   openRowNodeId, onOpenRow(row)
 *   …shell               the SidePanelStack's props for this level
 *
 * Must render inside a SignalCategoriesProvider (the body reads the theme).
 */
// The drilldown's header band bleeds to the panel's edges: the body's own
// padding, undone by the band (see HeaderBand in CardGridLayout).
const BODY_PAD = { x: "20px", y: "16px" };

const BodySkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <SkeletonLoader style={{ height: "12rem", borderRadius: 8 }} />
    <SkeletonLoader style={{ height: "14rem", borderRadius: 8 }} />
    <SkeletonLoader style={{ height: "14rem", borderRadius: 8 }} />
  </div>
);

export const TopicDrilldownPanel = ({ isOpen, width, topicNodeId, scope, titleByContentType, openRowNodeId, onOpenRow, ...shell }) => {
  const { period, comparePeriod, extraFilters, topicClusteringLens, dateFieldMap } = scope;
  const activeFilters = useMemo(() => activeFiltersFor(period, extraFilters), [period, extraFilters]);
  const { data: counts, isLoading } = useGetTopicClusteringTreeCounts({
    topicClusteringLens,
    activeFilters,
    enabled: isOpen && !!period,
  });
  const tree = useMemo(() => toCardTree(counts), [counts]);
  const bucket = useMemo(() => tree?.tree.find((b) => String(b.nodeId) === String(topicNodeId)) || null, [tree, topicNodeId]);

  // The "vs last period" chips compare with the selection's comparison period.
  const prevWindow = useMemo(
    () => (comparePeriod ? { from: comparePeriod.from, to: comparePeriod.to, label: comparePeriod.display } : null),
    [comparePeriod]
  );
  const prevScope = useMemo(
    () => (prevWindow ? { topicClusteringLens, activeFilters: previousWindowFiltersFor(prevWindow, extraFilters) } : null),
    [prevWindow, topicClusteringLens, extraFilters]
  );
  const drilldownScope = useMemo(
    () => ({ activeFilters, topicClusteringLens, period, dateFieldMap, quickSummary: { compareKey: comparePeriod ? periodKey(comparePeriod) : null } }),
    [activeFilters, topicClusteringLens, period, dateFieldMap, comparePeriod]
  );

  return (
    <SidePanel
      {...shell}
      isOpen={isOpen}
      width={width}
      title={<UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>{`Topic · ${period?.display || ""}`}</UI_CAPTION_SEMIBOLD_DF>}
      bodyStyle={{ background: colors2.grey3, padding: `${BODY_PAD.y} ${BODY_PAD.x} 24px` }}
    >
      {isOpen && (isLoading || !tree) && <BodySkeleton />}
      {isOpen && tree && !bucket && !isLoading && (
        <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>Nothing in this topic for {period?.display || "this period"}.</UI_CAPTION_SEMIBOLD_DF>
      )}
      {isOpen && bucket && (
        <CardGridLayout
          tree={tree}
          signalMetaById={counts?.signalMetaById || {}}
          titleByContentType={titleByContentType}
          selectedBucket={bucket}
          onSelectBucket={() => {}}
          drilldownBackButton={false}
          onRowClick={onOpenRow}
          openRowNodeId={openRowNodeId}
          period={period}
          prevWindow={prevWindow}
          prevScope={prevScope}
          drilldownScope={drilldownScope}
          headerBleed={{ ...BODY_PAD, top: 0 }}
        />
      )}
    </SidePanel>
  );
};
